import { z } from "zod/v4";
import { outdent } from "@repo/utils/utils";

//#region src/harnesses/production/sharedTools.ts
const clientSideError = z.object({
	success: z.literal(false),
	code: z.enum([
		"REJECTED_BY_USER",
		"INPUT_VALIDATION_ERROR",
		"OUTSIDE_BUSINESS_HOURS",
		"USER_ACTION_REQUIRED",
		"INTERNAL_ERROR",
		"FORBIDDEN"
	]),
	message: z.string(),
	suggestion: z.string().optional()
});
/**
* `clientSideError` plus the wait a closed office implies.
*
* Extended here rather than on `clientSideError` itself: several other tools
* share that schema and have no business hours to report.
*/
const phoneCallError = clientSideError.extend({
	nextOpenTime: z.string().optional(),
	snoozeFor: z.string().optional(),
	snoozeDuration: z.object({
		days: z.number(),
		hours: z.number(),
		minutes: z.number()
	}).optional(),
	snoozeUntil: z.string().optional()
});
const phoneCallOutputSchema = z.discriminatedUnion("success", [z.object({
	success: z.literal(true),
	message: z.string(),
	callId: z.string(),
	provider: z.enum([
		"vapi",
		"retell",
		"vogent"
	])
}), phoneCallError]);
const browserAgentJobOutputSchema = z.discriminatedUnion("success", [
	z.object({
		success: z.literal(true),
		jobId: z.string(),
		status: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("JOB_FAILED"),
		message: z.string()
	}),
	clientSideError
]);
const sendMessageOutputSchema = z.discriminatedUnion("success", [
	z.object({
		success: z.literal(true),
		consentStatus: z.enum([
			"consented",
			"requested",
			"consent-request-in-flight"
		]),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("CONSENT_REQUEST_IN_FLIGHT"),
		consentStatus: z.literal("consent-request-in-flight"),
		lastRequestedAt: z.string(),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("CONSENT_DECLINED"),
		consentStatus: z.literal("declined"),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("JOB_ALREADY_RUNNING"),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("USER_ACTION_REQUIRED"),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("OUTSIDE_BUSINESS_HOURS"),
		message: z.string(),
		snoozeFor: z.string().optional(),
		snoozeDuration: z.object({
			days: z.number(),
			hours: z.number(),
			minutes: z.number()
		}).optional(),
		nextOpenTime: z.string().optional(),
		snoozeUntil: z.string().optional()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("MESSAGE_NOT_ALLOWED"),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("NOTIFICATION_SKIPPED"),
		message: z.string()
	}),
	z.object({
		success: z.literal(false),
		code: z.literal("FORBIDDEN"),
		message: z.string()
	}),
	clientSideError
]);
const providerWithDetails = z.object({
	id: z.string(),
	npi: z.string().nullable(),
	providerName: z.string(),
	offersTelehealth: z.boolean().nullable(),
	emailAddress: z.string().nullable(),
	gender: z.string().nullable(),
	age: z.number().nullable(),
	locations: z.array(z.object({
		address: z.string(),
		city: z.string().nullable(),
		state: z.string().nullable(),
		zip: z.string().nullable(),
		phoneNumber: z.string().nullable(),
		faxNumber: z.string().nullable()
	})),
	specialties: z.array(z.string()),
	organizations: z.array(z.object({
		id: z.string(),
		name: z.string(),
		acceptsInsurance: z.string(),
		providerNpiForReferralAuth: z.string().nullable(),
		isCca: z.boolean().nullable(),
		qualityTier: z.number().nullable(),
		ownershipType: z.string().nullable(),
		availability: z.string().nullable(),
		availabilityUpdatedAt: z.string().nullable(),
		specialtyDetails: z.string().nullable(),
		specialtyDetailsUpdatedAt: z.string().nullable(),
		providerAvailability: z.string().nullable(),
		providerAvailabilityUpdatedAt: z.string().nullable(),
		providerSpecialtyDetails: z.string().nullable(),
		providerSpecialtyDetailsUpdatedAt: z.string().nullable()
	}))
}).strict();
const searchProvidersOutputSchema = z.discriminatedUnion("success", [z.object({
	success: z.literal(true),
	message: z.string(),
	providers: z.array(providerWithDetails),
	total: z.number()
}), z.object({
	success: z.literal(false),
	message: z.string(),
	code: z.enum([
		"SPECIALTY_NOT_FOUND",
		"SPECIALTY_AMBIGUOUS",
		"INSURANCE_NOT_FOUND",
		"INSURANCE_AMBIGUOUS",
		"PROVIDER_NAME_NOT_FOUND",
		"PROVIDER_NAME_AMBIGUOUS",
		"ORGANIZATION_NOT_FOUND",
		"ORGANIZATION_AMBIGUOUS",
		"NO_PROVIDERS_FOUND",
		"NO_PROVIDERS_IN_ORGANIZATION",
		"DEMO_PHONES_UNCONFIGURED"
	]),
	suggestions: z.array(z.union([
		z.object({
			id: z.string(),
			name: z.string(),
			description: z.string().optional(),
			matchScore: z.number().optional()
		}),
		z.object({
			id: z.string(),
			planName: z.string().optional(),
			payor: z.string().optional(),
			planType: z.string().optional(),
			payorAbbreviation: z.string().optional(),
			matchScore: z.number().optional()
		}),
		z.object({
			id: z.string(),
			providerName: z.string(),
			npi: z.string().optional(),
			matchScore: z.number().optional()
		}),
		z.object({
			id: z.string(),
			name: z.string(),
			type: z.string().optional(),
			matchScore: z.number().optional()
		})
	])).optional()
})]);
/**
* Send-referral provider fax. Real tenants require a number. Demo may omit it
* when `sendRealFax` is false so canned send can succeed without a fax.
*/
function attachRecordsSendReferralInputSchema(requireFax) {
	return z.object({
		relevantNotes: z.string().describe("Any relevant notes about the referral (specialty, reason for referral, etc.)").optional(),
		provider: z.object({
			name: z.string().describe("Name of the provider or if that's not available, the office/organization that the referral is being sent to, (e.g., 'Dr. John Smith' or 'Forefront Orthopedics')"),
			addressLine1: z.string().describe("Specialist provider's street address"),
			city: z.string().describe("Specialist provider's city"),
			state: z.string().describe("Specialist provider's state (the full name of the state, e.g., North Carolina)"),
			zipCode: z.string().describe("Specialist provider's zip code"),
			faxNumber: requireFax ? z.string().min(1, "Expected valid fax number, but fax number is empty").describe("Specialist provider's fax number") : z.string().optional().default("").describe("Specialist provider's fax number"),
			providerId: z.string().optional().describe("The recipient's directory providerId, copied from the provider search result. Omit if the recipient did not come from a search."),
			organizationId: z.string().optional().describe("The directory organizationId of the organization you are sending to, copied from the same provider search result. Omit if the recipient did not come from a search.")
		}).describe("Information about the specialist the patient is being referred to - can be specified by individual provider or an organization"),
		priorAuthorizationDetails: z.object({
			priorAuthRequired: z.literal(true),
			authorizationNumber: z.string().describe("Prior authorization approval number"),
			numberOfVisitsAllowed: z.string().describe("Number of visits allowed under this authorization"),
			validFromDate: z.string().describe("Date from which the prior authorization is valid (MM/DD/YYYY format)"),
			validToDate: z.string().describe("Date until which the prior authorization is valid (MM/DD/YYYY format)"),
			providerName: z.string().describe("Name of the specialist on the prior authorization form"),
			providerNpi: z.string().describe("NPI of the specialist on the prior authorization form")
		}).describe("An object containing prior authorization information for the referral if `priorAuthRequired` is true.").or(z.object({ priorAuthRequired: z.literal(false) })).or(z.object({ priorAuthRequired: z.literal("Unknown") })).describe("Prior authorization information for the referral. If it is not explicitly stated whether prior authorization is required, then `priorAuthRequired` should be set to 'Unknown'. If `priorAuthRequired` is true, then the prior authorization details must be provided. If `priorAuthRequired` is false, then prior authorization details are not needed.")
	});
}
const sharedTools = {
	call_provider: {
		description: outdent(`
			Call a healthcare provider to ask them questions, gather information, or book appointments. This tool is limited to only calling medical provider offices, you CANNOT call anyone else.

			DO NOT add any unnecessary context to your questions like the patients name, age, etc. The logic within the tool itself adds context about the patient and referral.

			Formatting: The 'query' parameter must be a list of instructions for the agent making the phone call. Each instruction must start with an imperative verb such as "Ask", "Verify", "Confirm", or "Book".

			This tool can handle:
			- Insurance acceptance verification
			- Service availability and hours
			- Provider names and NPI numbers
			- Getting office details like fax number and address
			- Referral requirements
			- General office policies
			- Complete appointment booking process

			Examples:

			<example>
				<input>
					Context: Patient's name is John Smith, 44 years old, has Aetna Open Access Plus insurance. He has an open cardiology referral. Diagnosis codes are 41201, 41200, 41202.
					user: "Call Dr. Clapton's office at 7044843923 to ask if they take patient insurance and if they do book an appointment for the patient"
				</input>
				<arguments>
					phoneNumber: "7044843923"
					call_receiver: "Dr. Clapton's office"
					query: "1. Ask if they accept Aetna's Open Access Plus insurance\n2. If they accept patient's insurance, book an appointment for the patient"
				</arguments>
			</example>

			<example>
				<input>
					Context: Patient's name is Eric Johnson, 30 years old.
					user: "Call 1393940333 and ask if they have availability in the next month for a physical therapy consultation"
				</input>
				<arguments>
					phoneNumber: "1393940333"
					query: "1. Ask if they have availability in the next month for a physical therapy consultation"
				</arguments>
			</example>
		`),
		inputSchema: z.object({
			phoneNumber: z.string().describe("Provider's phone number in any format without country code"),
			callReceiver: z.string().optional().describe("Information about who is being called"),
			query: z.string().describe("Ordered list of things to talk about with the provider's office staff. For example, '1. Ask if they have availability next month for a physical therapy consultation'")
		}),
		outputSchema: phoneCallOutputSchema,
		confirmationRequired: true
	},
	search_healthcare_providers: {
		description: outdent(`
			Use this agent when you need to find healthcare providers based on specialty, location, provider names, or specific criteria like telehealth availability and demographics.

			Never infer a city or state that is not stated in the conversation, the
			doctor's notes, or the activity logs. This tool is shared across practices
			in different metros, so a guessed location is not a bad guess — it is a
			filter that silently excludes every provider near the patient. When no
			location is stated, omit the locations field entirely so the practice's
			own default applies.

			The examples below write locations as [city] and [ST] because the right
			value always comes from the conversation. Never pass a bracketed
			placeholder through as a real value: if [city] is all you have, you have
			no location, so leave locations out.

			Examples:

			<example>
			Context: Patient needs a specialist consultation in their area
			user: "Find dermatologists in [city], [ST]"
			assistant: "I'll search for dermatologists in [city], [ST] for you."
			<commentary>Use when users need specialty providers in specific locations. Pass through the city and state the user actually named, as a bare city name and a 2-letter state code.</commentary>
			</example>

			<example>
			Context: Patient has a specific provider recommendation
			user: "Find Dr. Smith in [city]"
			assistant: "I'll search for 'Smith' practicing in [city]."
			<commentary>Use when users need to locate specific providers by name and location. Remove titles like 'Dr.' from the search.</commentary>
			</example>

			<example>
			Context: Patient wants to find providers at a specific hospital or clinic
			user: "Find cardiologists at Mayo Clinic"
			assistant: "I'll search for cardiologists at Mayo Clinic."
			<commentary>Use when users need providers at specific healthcare organizations</commentary>
			</example>

			<example>
			Context: Parent seeking pediatric care with telehealth preference
			user: "Pediatricians near [city], [ST] with telehealth"
			assistant: "I'll search for pediatricians near [city] who offer telehealth services."
			<commentary>Use when users have specific service requirements like telehealth</commentary>
			</example>
		`),
		inputSchema: z.object({
			specialty: z.string().optional().describe("Medical specialty in natural language (e.g., 'dermatology', 'cardiology', 'orthopedic surgery')"),
			locations: z.array(z.object({
				city: z.string().optional(),
				state: z.string().regex(/^[A-Z]{2}$/, "2-letter US state code").optional()
			})).optional().describe("Array of location objects with optional city and state properties. Leave as undefined if there's no clear location information — this is a hard filter, so a guessed or placeholder city returns no providers at all rather than nearby ones. A city without a state is searched in the states around the clinic, so pass the state whenever the conversation names one. For some small towns, a specialty search that finds nobody in the town automatically retries in nearby towns and then with telehealth providers in the state; the results say so and mark each provider outside the town."),
			providerNpi: z.string().length(10, "NPI must be 10 characters").optional().describe("National Provider Identifier (NPI) for a specific provider (e.g., '1234567890')"),
			providerName: z.string().optional().describe("Name of a specific provider to search for. Use only the provider's actual name without prefixes like 'Dr.' (e.g., 'Smith', 'John Doe', 'Patel')"),
			organizationName: z.string().optional().describe("Name of a specific organization to search for (e.g., 'Mayo Clinic', 'St. Lukes Hospital', 'Forefront Orthopedics')"),
			acceptsTelehealth: z.boolean().optional().describe("Filter for providers that offer telehealth services"),
			gender: z.enum(["male", "female"]).optional().describe("Filter for providers by gender"),
			ageFilter: z.object({
				minAge: z.number().int().optional(),
				maxAge: z.number().int().optional()
			}).optional().describe("Filter for providers by age range"),
			limit: z.number().min(1).max(100).default(5).optional().describe("Maximum number of providers to return"),
			offset: z.number().min(0).max(100).default(0).optional().describe("Offset for the number of providers to skip. Used for pagination, if the user wants to see more providers.")
		}),
		outputSchema: searchProvidersOutputSchema,
		confirmationRequired: false
	},
	submit_referral_prior_authorization: {
		description: outdent(`
			Submit a referral prior authorization request using automated browser agent.

			**Purpose**: Automate the submission of referral prior authorization requests to healthcare payers via insurance portals

            You should only need information about the specialist that the referral is being sent to. The other information will be automatically provided by the system based on the patient's medical data. It will only be needed if the prior authorization submission fails.

			Examples:

			<example>
			Context: Patient needs MRI scan requiring prior authorization
			user: "Submit a prior auth request for John's MRI for his back pain"
			assistant: "I'll submit the prior authorization request for John's MRI scan for back pain."
			<commentary>Use when medical procedures require insurance pre-approval</commentary>
			</example>

			<example>
			Context: Physical therapy referral needs authorization
			user: "Request prior auth for 12 PT sessions for knee rehabilitation"
			assistant: "I'll submit a prior authorization request for 12 physical therapy sessions for knee rehabilitation."
			<commentary>Use when therapy services require insurance authorization</commentary>
			</example>

			<example>
			Context: Specialty medication needs approval
			user: "Submit prior auth for the biologic medication Dr. Smith prescribed"
			assistant: "I'll submit the prior authorization request for the biologic medication prescription."
			<commentary>Use when specialty medications require insurance pre-approval</commentary>
			</example>

		`),
		inputSchema: z.object({
			provider: z.object({
				firstName: z.string().describe("Specialist provider's first name"),
				lastName: z.string().describe("Specialist provider's last name"),
				npi: z.string().regex(/^\d{10}$/, "Provider NPI must be a 10-digit number").describe("Specialist provider's NPI number"),
				addressLine1: z.string().describe("Specialist provider's street address"),
				city: z.string().describe("Specialist provider's city"),
				state: z.string().describe("Specialist provider's state (the full name of the state, e.g., North Carolina)"),
				zipCode: z.string().trim().regex(/^\d{5}(-?\d{4})?$/, "Provider ZIP code is required").describe("Specialist provider's zip code")
			}).describe("The specialist the patient is being referred to - can be specified by individual provider name or organization name"),
			diagnosisCodes: z.array(z.string()).optional().describe("Diagnosis codes (ICD-10) for the referral. Leave this empty to use the referral's saved codes. Pass codes only when a person wrote them, in the chat or in a comment on the task, and copy them exactly. Never infer codes from the referral reason."),
			primaryCareProviderNpi: z.string().optional().describe("The NPI of the patient's primary care provider who is making the referral. If the user explicitly mentions the primary care provider's name or NPI and there is a search provider tool call with the PCP's NPI as an output, then pass the PCP's NPI.")
		}),
		outputSchema: browserAgentJobOutputSchema,
		confirmationRequired: true
	},
	send_message_to_patient: {
		description: outdent(`
			Send a message to a patient via Spruce healthcare communications platform. The message will be sent through the patient's existing Spruce conversation or create a new one if needed.

			This tool will automatically handle patient consent for text message communication about referrals. The actual message sent may differ from what you request:
			- If the patient has already consented: Your message will be sent as-is
			- If the patient has declined consent: The tool will return an error
			- If patient consent is unknown: A consent request message will be sent automatically instead of the message you requested.

			Use this tool when you need to communicate directly with patients for:
			- Appointment reminders and confirmations
			- Follow-up questions about treatment or symptoms
			- Instructions or pre-visit requirements
			- General health updates and check-ins
			- Lab result notifications

			IMPORTANT:
			- DO NOT mention irrelevant information like your role.
			- Keep messages concise, but include information that will be important for the patient. For example when messaging a patient about a referral you sent to a specialist, you would include the specialist's (or their office's) name, the address, their phone number, and potentially information about the specialist's availability if available.
			- DO NOT send consent request messages, since they are handled automatically. Send the message actively requesting the information you need.
			- Always use the patient's first name only (not full name or last name).

			**Message Tailoring Based on Responsible Party:**
			All messages will be sent to the patient's responsible party. You must tailor your message appropriately:

			- If the patient IS the responsible party: Use direct address with "you" and "your"
			  Example: "Hi Mary, we're processing your dermatology referral. Do you have any preferences for a specific doctor or location?"

			- If the patient is NOT the responsible party: Use the patient's first name and third-person pronouns ("their", "them", patient's name)
			  Example: "Hi, we're processing Mary's dermatology referral. Does Mary have any preferences for a specific doctor or location?"

			**Language Translation:**
			- If the patient's primary language is NOT English, translate your entire message to that language
			- Translate all content including greetings, questions, instructions, and provider information
			- Keep medical terminology accurate and culturally appropriate
			- Maintain the same tone and level of formality in the translation

			Examples:
			<patient_is_responsible_party>
			    <message>Hi Sarah, this is regarding your cardiology referral. Can you confirm if you have any preferences for a specific cardiologist or location?</message>
			</patient_is_responsible_party>

			<patient_is_not_responsible_party>
			    <message>Hi, this is regarding Joe's cardiology referral. Does Joe have any preferences for a specific cardiologist or location?</message>
			</patient_is_not_responsible_party>

			<patient_primary_language_spanish>
			    <message>Hola Maria, estamos procesando tu referencia de cardiología. ¿Tienes alguna preferencia por un cardiólogo o ubicación específica?</message>
			</patient_primary_language_spanish>
		`),
		inputSchema: z.object({ message: z.string().min(1).describe("The message to send to the patient") }),
		outputSchema: sendMessageOutputSchema,
		confirmationRequired: true
	},
	attach_records_send_referral: {
		description: outdent(`
			This tool automatically reads through the patient's medical records and attaches the most relevant records to the referral. It then sends the referral along with the records via fax using an automated browser agent.

			A task can have only one send in progress, and a second request starts nothing. Check the result's status:
			- "ALREADY_RUNNING": a send to this same fax number is already underway, and the returned jobId is that send. Do not call this tool again; wait for that send's result.
			- "ALREADY_RUNNING_OTHER_RECIPIENT": a send to a different fax number is already underway, so the recipient you asked for was NOT sent to. Do not report it as sent. Say that another send for this task is in progress, and that this referral can be sent to the requested recipient once that one finishes.

			When the recipient came from a provider search, always pass that provider's directory ids as \`provider.providerId\` and \`provider.organizationId\`. They record which directory entry the referral went to. Copy them exactly from the search result (its "Directory ids" list, or the provider's \`id\` and \`organizations[].id\`); never guess or construct one. If the recipient did not come from a search, omit both.

			Examples:

			<example>
			Context: Patient needs cardiology referral sent with relevant records
			user: "Send John's referral to Dr. Martinez with his recent EKG and lab results"
			assistant: "I'll attach John's relevant medical records and send the referral to Dr. Martinez via fax."
			<commentary>Use when referrals need to be sent with supporting medical records</commentary>
			</example>

			<example>
			Context: Specialist needs complete patient history for consultation
			user: "Fax the orthopedic referral with all imaging studies to Dr. Thompson's office"
			assistant: "I'll gather the imaging studies and send the orthopedic referral to Dr. Thompson's office."
			<commentary>Use when specialists require comprehensive documentation</commentary>
			</example>

			<example>
			Context: Urgent referral with prior authorization needs to be sent
			user: "Send the neurology referral for Sarah with the prior auth approval"
			assistant: "I'll send Sarah's neurology referral with the prior authorization approval to the specialist."
			<commentary>Use when referrals include prior authorization information</commentary>
			</example>
		`),
		inputSchema: attachRecordsSendReferralInputSchema(true),
		outputSchema: browserAgentJobOutputSchema,
		confirmationRequired: true
	},
	check_patient_eligibility: {
		description: outdent(`
			Check patient insurance eligibility to verify patient insurance coverage and benefits.

			IMPORTANT: Eligibility checks are generally only run on the primary insurance for a patient. If a patient has multiple insurances and the user did not specify which insurance they want to check eligibility for, ONLY run it for the primary insurance.

			Examples:

			<example>
			Context: Patient scheduled for procedure and needs insurance verification
			user: "Check if John's insurance is active before his surgery next week"
			assistant: "I'll check John's insurance eligibility to verify coverage before his surgery."
			<commentary>Use when verifying coverage for scheduled procedures</commentary>
			</example>

			<example>
			Context: New patient visit requires eligibility verification
			user: "Verify Sarah's Aetna coverage for her appointment today"
			assistant: "I'll check Sarah's Aetna insurance eligibility for today's appointment."
			<commentary>Use when confirming coverage for patient appointments</commentary>
			</example>

			<example>
			Context: Patient concerns about insurance coverage
			user: "Can you check if my insurance is still valid? I think it might have expired"
			assistant: "I'll check your insurance eligibility to verify if your coverage is still active."
			<commentary>Use when patients question their coverage status</commentary>
			</example>
		`),
		inputSchema: z.object({}),
		outputSchema: browserAgentJobOutputSchema,
		confirmationRequired: true
	},
	update_patient_info: {
		description: outdent(`
			Update patient information in the system. Supports updating:
			- Text message consent status
			- Primary language preference

            Can update any combination of the supported fields in a single tool call, each field is optional.

            IMPORTANT: Only use this tool when a user in the activity logs explicitly mentions updating the patient's consent status or language preference.

			This tool ONLY updates the patient's information in system's database. It does NOT perform any other actions, like sending messages or consent requests.

			Examples:

			<example>
			Context: Staff member left a comment "pt consented" after speaking with the patient
			assistant: "I'll update the patient's consent status to reflect the verbal consent obtained by staff."
			Arguments: { "consentStatus": "consented" }
			</example>

			<example>
			Context: Staff member noted "patient prefers Spanish"
			assistant: "I'll update the patient's primary language to Spanish."
			Arguments: { "primaryLanguage": "Spanish" }
			</example>

			<example>
			Context: Staff member noted "pt consented, Spanish speaking"
			assistant: "I'll update both the consent status and language preference."
			Arguments: { "consentStatus": "consented", "primaryLanguage": "Spanish" }
			</example>
		`),
		inputSchema: z.object({
			consentStatus: z.enum(["consented", "declined"]).optional().describe("The patient's new consent status for text message communication"),
			primaryLanguage: z.enum(["English", "Spanish"]).optional().describe("The patient's preferred language for communication")
		}),
		outputSchema: z.discriminatedUnion("success", [z.object({
			success: z.literal(true),
			message: z.string(),
			updatedFields: z.array(z.string())
		}), z.object({
			success: z.literal(false),
			code: z.enum([
				"UPDATE_FAILED",
				"INVALID_INPUT",
				"NO_FIELDS_TO_UPDATE"
			]),
			message: z.string()
		})]),
		confirmationRequired: true
	}
};

//#endregion
export { attachRecordsSendReferralInputSchema, searchProvidersOutputSchema, sharedTools };